import { useMemo } from "react";
import {
  useSession,
  useSessionEntityLookup,
} from "@/contexts/session/data/hooks";
import type { CreferenceEntityType } from "./CreferenceEntity";
import {
  selectCreferenceEntityType,
  selectCreferenceResolvedEntities,
  selectIsCurrentUserReference,
  type ReferenceCollab,
  type ReferenceFile,
  type ReferenceMember,
  type ReferenceProduct,
  type ReferenceVersion,
} from "./creferenceResolvedEntitySelectors";

interface UseCreferenceResolvedLookupInput {
  normalizedEntityId: string;
}

export interface CreferenceResolvedLookup {
  collab: ReferenceCollab | null;
  file: ReferenceFile | null;
  effectiveEntityType: CreferenceEntityType;
  isCurrentUserReference: boolean;
  member: ReferenceMember | null;
  product: ReferenceProduct | null;
  sessionLoading: boolean;
  version: ReferenceVersion | null;
  /** Resolved only for version references: the product the version belongs to.
   *  Kept OFF `product` deliberately — that field drives the leading icon and
   *  the product click action, and a version must keep both of its own. */
  versionProduct: ReferenceProduct | null;
}

export function useCreferenceResolvedLookup({
  normalizedEntityId,
}: UseCreferenceResolvedLookupInput): CreferenceResolvedLookup {
  const { resolveShortId, resolveProductById } = useSessionEntityLookup();
  const { session, loading: sessionLoading } = useSession();
  const resolved = resolveShortId(normalizedEntityId);
  const { collab, file, member, product, version } =
    selectCreferenceResolvedEntities(resolved);
  const versionProduct = version ? resolveProductById(version.productId) : null;
  const sessionUserId = session?.userId ?? null;
  const isCurrentUserReference = selectIsCurrentUserReference({
    resolved,
    sessionUserId,
  });
  const effectiveEntityType = useMemo(
    () => selectCreferenceEntityType(resolved),
    [resolved],
  );

  return {
    collab,
    file,
    effectiveEntityType,
    isCurrentUserReference,
    member,
    product,
    sessionLoading,
    version,
    versionProduct,
  };
}
